import { useAuth0 } from "@auth0/auth0-react";
import { customClient } from "@utils/helpers";
import { useState } from "react";

import { GetTranscriptCsvContentDocument, GetTranscriptCsvContentQueryVariables } from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { CommentCount } from "@pages/VideoSessionsV2/components/EmojiComments/EmojiCommentButtons";

export const convertTimestampToSeconds = (timestamp: string): number => {
  const [hh, mm, ss] = timestamp.split(":").map(Number);
  return hh * 3600 + mm * 60 + ss;
};

export const getEmojiCount = (value: string, commentsCount?: string): number => {
  if (!commentsCount) return 0;
  const parsedCounts: CommentCount[] = JSON.parse(commentsCount);
  return parsedCounts.find((comment) => comment.comment_type.toLowerCase() === value.toLowerCase())?.count || 0;
};

/**
 * Custom hook that provides a function to download transcript CSV from server
 * @returns Function to download transcript CSV and loading state
 */
export const useDownloadTranscriptCSV = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const downloadTranscriptCSV = async (videoSessionId: string): Promise<void> => {
    if (!videoSessionId) {
      console.error("Video Session ID is required to download transcript");
      return;
    }

    setIsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const variables: GetTranscriptCsvContentQueryVariables = { videoSessionId };

      const response = await customClient({
        token,
        document: GetTranscriptCsvContentDocument,
        variables,
      });

      const result = response.getTranscriptCsvContent;

      if (!result?.status || !result.csvContent) {
        console.error("Failed to get transcript download URL:", result?.message || "Unknown error");
        notifyError(result?.message || "Unknown error");
        return;
      }

      const csvContent = result.csvContent;

      // Create Blob from CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const blobUrl = URL.createObjectURL(blob);

      // Create download link and trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = result.filename || `transcript_${videoSessionId}.csv`;
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL to free memory
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download transcript CSV:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { downloadTranscriptCSV, isLoading };
};
